import { of, NEVER, shareReplay, Subject, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as i0 from '@angular/core';
import { Optional, LOCALE_ID, Pipe, Inject, NgModule, Injectable, PLATFORM_ID } from '@angular/core';
import * as i1$1 from '@angular/common/http';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import * as i1 from '@angular/common';
import { DatePipe, isPlatformBrowser, DOCUMENT } from '@angular/common';
import * as i1$2 from '@angular/platform-browser';
import * as i1$3 from 'ngx-cookie-service-ssr';

class CrudEndpointBase {
    _service;
    basePath;
    constructor(httpService, basePath) {
        this._service = httpService;
        this.basePath = basePath;
    }
    get service() {
        return this._service;
    }
    get path() {
        return this.basePath;
    }
    fetchById(id) {
        return this._service.get(this.basePath + '/' + id);
    }
    fetchByIdPlain(id) {
        return this._service.getPlain(this.basePath + '/' + id);
    }
    fetchPage(event, params) {
        return this._service.paging(this.basePath + '/page', event, params);
    }
    fetchPagePlain(event, params) {
        return this._service.pagingPlain(this.basePath + '/page', event, params);
    }
    save(entity) {
        return this._service.post(this.basePath, entity);
    }
    savePlain(entity) {
        return this._service.postPlain(this.basePath, entity);
    }
    deleteById(id) {
        return this._service.delete(this.basePath + '/' + id);
    }
    deleteByEntity(entity) {
        return this._service.deleteContent(this.basePath, entity);
    }
}

var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
/**
 * @deprecated Use angular ResolverFn instead.
 */
let DefaultDataResolver = class DefaultDataResolver {
    urlResolver;
    dataService;
    elseData;
    router;
    constructor(urlResolver, dataService, elseData, router) {
        this.urlResolver = urlResolver;
        this.dataService = dataService;
        this.elseData = elseData;
        this.router = router;
    }
    resolve(route, state) {
        return this.dataService.get(this.urlResolver(route)).pipe(catchError(() => {
            if (this.router !== undefined)
                this.router.navigate(['/error']).then(() => { });
            return this.elseData !== null ? of(this.elseData) : NEVER;
        }));
    }
};
DefaultDataResolver = __decorate([
    __param(3, Optional())
], DefaultDataResolver);

class DefaultHttpService {
    baseUrl;
    http;
    defaultHeader = new HttpHeaders({
        'Content-type': 'application/json'
    });
    constructor(baseUrl, http) {
        this.baseUrl = baseUrl;
        this.http = http;
    }
    static propertyToUrl(data) {
        return `${data.matchMode}:${data.value}`;
    }
    getSignature(sigHeader, headers) {
        let signature = undefined;
        if (headers) {
            const matchingSignature = 'Arematics-' + sigHeader + '-Signature';
            if (headers.has(matchingSignature)) {
                signature = 'sig=' + headers.get(matchingSignature);
            }
        }
        return signature;
    }
    get(resourceUrl) {
        return this.http.get(this.baseUrl + resourceUrl, { headers: this.defaultHeader });
    }
    getPlain(resourceUrl) {
        return this.http.get(this.baseUrl + resourceUrl, { headers: this.defaultHeader, observe: 'response' });
    }
    getBlob(resourceUrl) {
        return this.http.get(this.baseUrl + resourceUrl, { responseType: 'blob' });
    }
    paging(path, event, params = new HttpParams()) {
        params = this.buildParams(event, params);
        return this.http.get(this.baseUrl + path, { headers: this.defaultHeader, params });
    }
    pagingPlain(path, event, params = new HttpParams()) {
        params = this.buildParams(event, params);
        return this.http.get(this.baseUrl + path, {
            headers: this.defaultHeader,
            params,
            observe: 'response'
        });
    }
    buildParams(event, params) {
        const size = event.rows || 10;
        if (!params.has('size'))
            params = params.set('size', size);
        if (!params.has('page'))
            params = params.set('page', (event.first || 0) / size);
        for (const property in event.filters) {
            if (event.filters.hasOwnProperty(property))
                params = params.set(property, DefaultHttpService.propertyToUrl(event.filters[property]));
        }
        return params;
    }
    postNoBody(url) {
        return this.http.post(this.baseUrl + url, { withCredentials: true, headers: this.defaultHeader });
    }
    post(url, resource) {
        return this.http.post(this.baseUrl + url, resource, { withCredentials: true, headers: this.defaultHeader });
    }
    postPlain(url, resource) {
        return this.http.post(this.baseUrl + url, resource, {
            withCredentials: true,
            headers: this.defaultHeader,
            observe: 'response'
        });
    }
    postFile(url, file) {
        const data = new FormData();
        data.append('file', file, file.name);
        return this.http.post(this.baseUrl + url, data, { withCredentials: true });
    }
    postPdf(url) {
        return this.http.post(this.baseUrl + url, {}, { responseType: 'blob' });
    }
    postForm(url, resource) {
        return this.http.post(this.baseUrl + url, resource, { withCredentials: true });
    }
    putForm(url, resource) {
        return this.http.put(this.baseUrl + url, resource, { withCredentials: true });
    }
    patch(url, resource) {
        return this.http.patch(this.baseUrl + url, resource, { withCredentials: true, headers: this.defaultHeader });
    }
    put(url, resource) {
        return this.http.put(this.baseUrl + url, resource, { withCredentials: true, headers: this.defaultHeader });
    }
    putPlain(url, resource) {
        return this.http.put(this.baseUrl + url, resource, {
            withCredentials: true,
            headers: this.defaultHeader,
            observe: 'response'
        });
    }
    delete(url) {
        return this.http.delete(this.baseUrl + url, { withCredentials: true, headers: this.defaultHeader });
    }
    deleteContent(url, resource) {
        return this.http.delete(this.baseUrl + url, {
            withCredentials: true,
            headers: this.defaultHeader,
            body: resource
        });
    }
    safePipe(observer, elseData) {
        return observer.pipe(catchError(() => of(elseData)));
    }
    client() {
        return this.http;
    }
    get root() {
        return this.baseUrl;
    }
}

/**
 * Arematics Course Endpoint
 * Documentation for the Arematics Learning Course Endpoint. Only working on arematics.com domains.
 *
 * OpenAPI spec version: 1.0.0
 * Contact: contact@arematics.com
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
var Level;
(function (Level) {
    Level["BEGINNER"] = "Beginner";
    Level["EASY"] = "Easy";
    Level["NORMAL"] = "Normal";
    Level["HARD"] = "Hard";
    Level["VERY_HARD"] = "Very Hard";
})(Level || (Level = {}));
function ordinal(level) {
    const key = Object.keys(Level).filter((k) => k === level)[0];
    if (!key)
        return 0;
    // @ts-ignore
    switch (Level[key]) {
        case Level.EASY:
            return 1;
        case Level.NORMAL:
            return 2;
        case Level.HARD:
            return 3;
        case Level.VERY_HARD:
            return 4;
        default:
            return 0;
    }
}

class LocalizedDatePipe {
    date;
    locale;
    constructor(date, locale) {
        this.date = date;
        this.locale = locale;
    }
    transform(value, format = 'longDate') {
        return this.date.transform(value, format, undefined, this.locale);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LocalizedDatePipe, deps: [{ token: i1.DatePipe }, { token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: LocalizedDatePipe, name: "localizedDate" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LocalizedDatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'localizedDate'
                }]
        }], ctorParameters: function () { return [{ type: i1.DatePipe }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; } });
class LocalizedDatePipeModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LocalizedDatePipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: LocalizedDatePipeModule, declarations: [LocalizedDatePipe], exports: [LocalizedDatePipe] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LocalizedDatePipeModule, providers: [DatePipe] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LocalizedDatePipeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [LocalizedDatePipe],
                    exports: [LocalizedDatePipe],
                    providers: [DatePipe]
                }]
        }] });

const maxMinutes = 5 * 60 * 1000;
class NameService {
    environment;
    http;
    cache = {};
    constructor(environment, http) {
        this.environment = environment;
        this.http = http;
    }
    fetchName(uuid) {
        return this.http.get(this.environment.data_api + `profile/${uuid}/name`).pipe(map((response) => response.name), shareReplay({ bufferSize: 1, refCount: true, windowTime: maxMinutes }));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NameService, deps: [{ token: 'environment' }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NameService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NameService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }, { type: i1$1.HttpClient }]; } });

class SafePipe {
    sanitizer;
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(value, type) {
        switch (type) {
            case 'html':
                return this.sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this.sanitizer.bypassSecurityTrustUrl(value);
            case 'resourceUrl':
                return this.sanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                throw new Error(`Unable to bypass security for invalid type: ${type}`);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafePipe, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SafePipe, name: "safe" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'safe'
                }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }]; } });

class StringReplacePipe {
    constructor() { }
    transform(input, from, to) {
        input = input || '';
        from = from || '';
        to = to || '';
        return input.replace(new RegExp(from, 'g'), to);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StringReplacePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: StringReplacePipe, name: "strReplace" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StringReplacePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'strReplace'
                }]
        }], ctorParameters: function () { return []; } });

class PipesModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: PipesModule, declarations: [SafePipe, StringReplacePipe], exports: [SafePipe, StringReplacePipe] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PipesModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PipesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SafePipe, StringReplacePipe],
                    exports: [SafePipe, StringReplacePipe]
                }]
        }] });

class SecurePipe {
    http;
    sanitizer;
    constructor(http, sanitizer) {
        this.http = http;
        this.sanitizer = sanitizer;
    }
    transform(url) {
        return this.http
            .get(url, { responseType: 'blob' })
            .pipe(map((val) => this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(val))));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SecurePipe, deps: [{ token: i1$1.HttpClient }, { token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SecurePipe, name: "secure" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SecurePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'secure'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: i1$2.DomSanitizer }]; } });
class SecurePipeModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SecurePipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SecurePipeModule, declarations: [SecurePipe], exports: [SecurePipe] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SecurePipeModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SecurePipeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SecurePipe],
                    exports: [SecurePipe]
                }]
        }] });

class UtilsService {
    document;
    platformId;
    constructor(document, platformId) {
        this.document = document;
        this.platformId = platformId;
    }
    downloadAsJson(data, name) {
        const theJSON = JSON.stringify(data, null, '\t');
        const url = 'data:text/json;charset=UTF-8,' + encodeURIComponent(theJSON);
        const element = this.document.createElement('a');
        element.setAttribute('href', url);
        element.setAttribute('download', `${name}.json`);
        this.document.body.appendChild(element);
        element.click();
        this.document.body.removeChild(element);
    }
    replaceLanguagePart(url, oldLocale, newLocale) {
        const firstSlashIndex = url.indexOf('/', 8);
        if (oldLocale === 'en-US') {
            return `${url.slice(0, firstSlashIndex)}/${newLocale}${url.slice(firstSlashIndex)}`;
        }
        const newValue = newLocale === 'en-US' ? '/' : '/' + newLocale + '/';
        return url.replace(`/${oldLocale}/`, newValue);
    }
    downloadFile(blob, name) {
        if (isPlatformBrowser(this.platformId)) {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = name + '.' + blob.type; // Provide the name you want
            link.click();
            // It's important to revoke the object URL to avoid memory leaks
            URL.revokeObjectURL(url);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UtilsService, deps: [{ token: DOCUMENT }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UtilsService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UtilsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

class ArematicsConfig {
    translation = {
        yes: 'Yes',
        no: 'No',
        multipleText: 'ore multiple',
        multipleTextAddition: 's'
    };
    translationSource = new Subject();
    getTranslation(key) {
        return this.translation[key];
    }
    setTranslation(value) {
        this.translation = { ...this.translation, ...value };
        this.translationSource.next(this.translation);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArematicsConfig, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArematicsConfig, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ArematicsConfig, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class LegalService extends DefaultHttpService {
    locale;
    constructor(http, locale, environment) {
        super(environment.data_api, http);
        this.locale = locale;
    }
    getLegal(type, translation) {
        return this.get('legal/' + type + (translation ? '?translation=' + translation : ''));
    }
    saveLegal(document) {
        return this.post('legal', document);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LegalService, deps: [{ token: i1$1.HttpClient }, { token: LOCALE_ID }, { token: 'environment' }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LegalService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LegalService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }]; } });

var LegalType;
(function (LegalType) {
    LegalType["PRIVACY"] = "PRIVACY";
    LegalType["REVOCATION"] = "REVOCATION";
    LegalType["TOS"] = "TOS";
})(LegalType || (LegalType = {}));

class DarkModeService {
    cookies;
    darkMode = new BehaviorSubject(false);
    currentDate = new Date();
    cookieDelete;
    constructor(cookies) {
        this.cookies = cookies;
        this.cookieDelete = new Date(this.currentDate.getFullYear() + 2, this.currentDate.getMonth(), this.currentDate.getDay());
        const darkMode = this.cookies.get('darkMode');
        if (darkMode) {
            this.changeDarkMode(JSON.parse(darkMode));
        }
    }
    get selected() {
        return this.darkMode.value;
    }
    get darkModeObserve() {
        return this.darkMode.asObservable();
    }
    changeDarkMode(newValue) {
        this.darkMode.next(newValue);
        this.cookies.set('darkMode', JSON.stringify(newValue), this.cookieDelete, '/');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DarkModeService, deps: [{ token: i1$3.SsrCookieService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DarkModeService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DarkModeService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.SsrCookieService }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { ArematicsConfig, CrudEndpointBase, DarkModeService, DefaultDataResolver, DefaultHttpService, LegalService, LegalType, Level, LocalizedDatePipe, LocalizedDatePipeModule, NameService, PipesModule, SafePipe, SecurePipe, SecurePipeModule, StringReplacePipe, UtilsService, ordinal };
